import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList, Multiselect } from "react-widgets";
import { toastr } from "react-redux-toastr";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import { Lead } from "../../../../core/api/models";
import {
  getLeadTitle,
  useLead,
  DateFormats,
  CreateInterestedProject,
  LeadInterestedProject,
} from "../../../../core";
import "react-widgets/dist/css/react-widgets.css";
import api from "../../../../api";
import moment from "moment";
import ProjectAutoCompleteInput from "../../../../components/form-group/project-auto-complete-input";
import PropertyAutoCompleteInput from "../../../../components/form-group/property-auto-complete-input";
import { isObject } from "class-validator";
import { decodeHtmlEntities } from "../../../../utils/common";
import RemoteDataMultiSelectDropDown from "../../../../components/dropdown/RemoteDataMultiSelectDropDown";

interface ProjectOption {
  projectName: string,
  _id: string
}

const AddInterestedProject: React.FunctionComponent<{
  leadId?: string;
  leads?: Lead[];
  onClose?: () => void;
  onSuccess?: () => void;
  className?: string;
  interestedProject?: LeadInterestedProject;
}> = (props) => {
  const { addInterestedProject, updateInterestedProject } = useLead(api.lead);
  const {
    control,
    handleSubmit,
    errors,
    reset,
    watch,
    setValue,
  } = useForm<CreateInterestedProject>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const [isSubmiting, setIsSubmiting] = React.useState<boolean>(false);
  const [projectLoading, setProjectLoading] = React.useState<boolean>(false);
  const [propertyLoading, setPropertyLoading] = React.useState<boolean>(false);
  const [projectOptions, setProjectOptions] = React.useState<{ value: string; name: string }[]>([]);
  const [propertyOptions, setPropertyOptions] = React.useState<{ value: string; name: string }[]>([]);

  useEffect(() => {
    const fetchProjectData = async () => {
      setProjectLoading(true)
      try {
        const projectData = await api.project.getProjectByKey('');
        const formattedProjectOptions: { value: string; name: string }[] = projectData?.map((item: ProjectOption) => ({
          value: item?._id,
          name: decodeHtmlEntities(item?.projectName),
        })) || [];
        // projectOptions?.sort((a, b) => a.name.localeCompare(b.name));
        setProjectOptions(formattedProjectOptions);
      } catch (error) {
        // console.error("Failed to fetch project options:", error);
      } finally {
        setProjectLoading(false);
      }
    }
    fetchProjectData();
  }, []);

  useEffect(() => {
    const fetchPropertyData = async () => {      
      setPropertyLoading(true)
      try {
        const query = ''
        const propertyData = await api.property.getProperyByKey(query, project || [])
        const formattedPropertyOptions: { value: string; name: string }[] = propertyData?.map((item: { propertyName: string; _id: string }) => ({
          value: item?._id,
          name: decodeHtmlEntities(item?.propertyName),
        })) || [];
        // projectOptions?.sort((a, b) => a.name.localeCompare(b.name));        
        setPropertyOptions(formattedPropertyOptions);
      } catch (error) {
        // console.error("Failed to fetch property options:", error);
      } finally {
        setPropertyLoading(false);
      }
    }
    if (watch("projectId") && watch("projectId")?.length) {
      fetchPropertyData();
    }
  }, [watch("projectId")]);

  const onSubmit = async (data: CreateInterestedProject) => {
    const updateData: CreateInterestedProject = {
      projectId: data?.projectId,
      propertyId: data?.propertyId,
    };
    setIsSubmiting(true);
    if (props.interestedProject && props.interestedProject?._id) {
      updateData._id = props.interestedProject?._id;
      updateInterestedProject(
        updateData,
        data.leadId,
        () => {
          toastr.success("Success", "Interested project updated successfully");
          reset({
            projectId: [],
            propertyId: [],
          });
          props.onSuccess?.();
          setIsSubmiting(false);
        },
        () => {
          setIsSubmiting(false);
        }
      );
    } else {
      addInterestedProject(
        updateData,
        data.leadId,
        () => {
          toastr.success("Success", "Interested project added successfully");
          reset({
            projectId: [],
            propertyId: [],
          });
          props.onSuccess?.();
          setIsSubmiting(false);
        },
        () => {
          setIsSubmiting(false);
        }
      );
    }
  };

  const project = isObject(watch("projectId"))
    ? (watch("projectId") as any)._id
    : watch("projectId");

  if (!props.leadId && !props.leads?.length) {
    return <></>;
  }

  const renderLeadOption = () => {
    if (props.leadId) {
      return (
        <Controller
          control={control}
          rules={{ required: "Lead is Required" }}
          name="leadId"
          defaultValue={props.leadId}
          render={() => <></>}
        />
      );
    }
    return (
      <div className="form-group font-weight-semibold mb-3">
        <label htmlFor="" className="font-sm text-uppercase">
          Lead<span className="text-danger">*</span>
        </label>
        <Controller
          control={control}
          rules={{ required: "Lead is required" }}
          name="leadId"
          defaultValue={props.leads?.[0]._id}
          render={({ onChange, value, name }) => (
            <DropdownList
              data={props.leads?.map((lead) => ({
                _id: lead._id,
                title: `${getLeadTitle(lead)} - Created on ${moment(
                  lead.createdAt
                ).format(DateFormats.NormalDateTimeFormat)} By ${lead?.createdBy?.name || "Alghanim website"
                  }`,
              }))}
              textField="title"
              value={value}
              defaultValue={value}
              valueField="_id"
              onChange={(val) => {
                onChange(val._id);
              }}
              placeholder="Select Lead"
            ></DropdownList>
          )}
        />
        <div>
          <ErrorMessage>{errors.leadId?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  return (
    <div className={`${props.className || ""} pt-2 pb-4`}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {renderLeadOption()}
        <div className="form-group font-weight-semibold mb-3">
          <label htmlFor="" className="font-sm text-uppercase">
            Project<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{ required: true }}
            name="projectId"
            // defaultValue={props.interestedProject?.project?._id? [ props.interestedProject?.project?._id]: []}
            render={({ onChange, value, name }) => (
              // <ProjectAutoCompleteInput
              //   defaultValue={value? value : []}
              //   placeholder={"Project"}
              //   onChange={(val:any) => {            
              //     const projectIds = val?.map((item: { _id: any; })=> item._id)
              //     onChange(projectIds);
              //   }}
              //   dataLoader={(query?: string) => {
              //     return api.project.getProjectByKey(query!);
              //   }}
              // />
              <Multiselect
                value={value}
                data={projectOptions}
                busy={projectLoading}
                textField={"name"}
                valueField={"value"}
                onChange={(val) => {
                  const projectIds = val?.map((item: { value: string; }) => item.value)
                  onChange(projectIds);
                }}
                filter="startsWith"
                messages={
                  projectOptions?.length === 0
                    ? { emptyList: "No data found" }
                    : {}
                }
                placeholder="Search a Project"
              />
            )}

          />

          {errors.projectId && <ErrorMessage>Project is required</ErrorMessage>}
        </div>

        <div className="form-group font-weight-semibold mb-3">
          <label htmlFor="" className="font-sm text-uppercase">
            Property
          </label>
          <Controller
            control={control}
            name="propertyId"
            // defaultValue={props.interestedProject?.property?._id ? [props.interestedProject?.property?._id] : []}
            render={({ onChange, value, name }) => (
              // <PropertyAutoCompleteInput
              //   defaultValue={value ? value : []}
              //   placeholder={"Property"}
              //   onChange={(val: any) => {
              //     const propertyIds = val?.map((item: { _id: any; }) => item._id)
              //     onChange(propertyIds);
              //   }}
              //   dataLoader={(query?: string) => {
              //     return api.property.getProperyByKey(query!, project || []);
              //   }}
              // />

              <Multiselect
                value={value}
                data={propertyOptions}
                busy={propertyLoading}
                textField={"name"}
                valueField={"value"}
                onChange={(val) => {
                  const propertyIds = val?.map((item: { value: string; }) => item.value)
                  onChange(propertyIds);
                }}
                filter="startsWith"
                messages={
                  projectOptions?.length === 0
                    ? { emptyList: "No data found" }
                    : {}
                }
                placeholder="Search a Property"
              />
            )}
          />

        </div>

        <div className="form-group font-weight-semibold mb-3 text-right pt-3 d-flex justify-content-between align-items-center">
          <div className="text-right">
            <button
              onClick={props.onClose}
              className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
              type="button"
            >
              Cancel
            </button>
            <button
              disabled={isSubmiting}
              className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddInterestedProject;
